import { useState, useCallback, useRef, useEffect } from "react";
import { getIdToken } from "../../../shared/apiService";
import { getPageSource } from "../../../shared/apiClient";
import { SmartColumnGenerateRequest } from "../../../shared/smartColumnApiService";
import { config } from "../../../shared/config";
import { useAnalysisGuard } from "../../hooks/useAnalysisGuard";

const API_BASE_URL = config.apiBaseUrl;

export default function useSmartColumnApiProgress() {
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const { setAnalysisInProgress } = useAnalysisGuard();

  useEffect(() => {
    setAnalysisInProgress(isLoading);
    return () => setAnalysisInProgress(false);
  }, [isLoading, setAnalysisInProgress]);

  const startGeneration = useCallback(async (request: SmartColumnGenerateRequest) => {
    setProgress(0);
    setResult(null);
    setIsLoading(true);

    try {
      const token = await getIdToken();
      const url = `${API_BASE_URL}/smart_column/generate`;

      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Page-Source": getPageSource(),
        },
      });

      const data = await response.json();

      const jobId = data.job_id;

      const esUrl = `${API_BASE_URL}/smart_column/progress/${jobId}?token=${token}`;

      const es = new EventSource(esUrl);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        console.log("event", event);
        const parsed = JSON.parse(event.data);
        console.log("parsed", parsed);

        if (parsed.progress !== undefined && parsed.progress !== null) {
          setProgress(parsed.progress);
        }

        if (parsed.status === "completed" && parsed.result) {
          setResult(parsed.result);
          setIsLoading(false);
          es.close();
        }

        if (parsed.status === "failed") {
          console.error("❌ Generation failed:", parsed.error);
          setResult({ error: parsed.error, status: "failed" });
          setIsLoading(false);
          es.close();
        }
      };

      es.onerror = (event) => {
        console.error("EventSource failed:", event);

        setResult({ error: "Streaming connection failed. Please try again.", status: "failed" });

        setIsLoading(false);
        eventSourceRef.current?.close();
        eventSourceRef.current = null;
      };
    } catch (error) {
      console.error("Stream error", error);
      setIsLoading(false);

      throw error;
    }
  }, []);

  const stopListening = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
  };

  return {
    progress,
    result,
    startGeneration,
    isLoading,
    stopListening,
  };
}
